/**
 * Supervisor task editor (TASK.2) — the missing "shape the work plan" surface.
 *
 * The work plan was frozen at PM intake: a supervisor reviewing or running a
 * project could reschedule and assign crew but never add a forgotten task,
 * drop a spurious one, or fix the crew-size / duration that drive the labor
 * estimate. This component fills that gap on the project workspace.
 *
 *   <AddTaskButton/>  — Tasks-card header → add a task (full type/subtype).
 *   <EditTaskButton/> — per-row pencil → edit title / crew size / duration /
 *                       description, and delete (when the task hasn't started).
 *
 * Type/subtype are fixed once a task exists (the PATCH contract doesn't move a
 * task across the discriminated union) — to change a task's type, delete and
 * re-add. Both modes share one portal dialog (bg-bg, matching the schedule and
 * approve dialogs). Supervisor/admin only — gated by the caller.
 */
import { useRef, useState } from 'react';
import { Camera, FileText, Image as ImageIcon, Loader2, Paperclip, Pencil, Plus, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import type {
  InstallSubtype,
  PickupLocation,
  PickupSubtype,
  Task,
  TaskCreateInput,
  TaskType,
} from '@vari-crew/schemas';
import { useCreateTask, useDeleteTask, useUpdateTask } from '@/api/tasks';
import { uploadTaskMedia } from '@/api/attachments';
import { useCurrentUser } from '@/auth/useCurrentUser';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { Input, Label, Textarea } from '@/components/ui/Input';
import { PickupLocationsEditor } from './PickupLocationsEditor';

const TYPE_OPTIONS: { value: TaskType; label: string }[] = [
  { value: 'pickup', label: 'Pickup' },
  { value: 'install', label: 'Install' },
  { value: 'punch', label: 'Punch' },
  { value: 'labor', label: 'Labor' },
];
const PICKUP_SUBTYPES: PickupSubtype[] = ['warehouse', 'supplier', 'rma'];
const INSTALL_SUBTYPES: InstallSubtype[] = ['new', 'reconfigure'];
const SELECT_CLS = 'w-full h-9 rounded-md border border-line bg-transparent px-2 text-sm';

/** Mirror the API delete guard so we never show a button that 409s. */
function taskDeletable(t: Pick<Task, 'status' | 'started_at' | 'completed_at'>): boolean {
  return (
    !t.started_at &&
    !t.completed_at &&
    (t.status === 'scheduled' || t.status === 'cancelled')
  );
}

function defaultSubtype(type: TaskType): string {
  if (type === 'pickup') return 'warehouse';
  if (type === 'install') return 'new';
  return '';
}

function subtypeOf(task: Task): string {
  if (task.type === 'pickup' || task.type === 'install') return task.subtype;
  return '';
}

export function AddTaskButton({ projectId }: { projectId: string }) {
  const [open, setOpen] = useState(false);
  const { data: me } = useCurrentUser();
  const isPm = me?.role === 'pm';
  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        <Plus className="w-3.5 h-3.5" />
        {isPm ? 'Request task' : 'Add task'}
      </Button>
      {open && <TaskEditorDialog projectId={projectId} onClose={() => setOpen(false)} />}
    </>
  );
}

export function EditTaskButton({
  task,
  onDeleted,
  variant = 'link',
}: {
  task: Task;
  /** Called after a successful delete — e.g. navigate away from a screen that
   *  was showing the now-deleted task. On a list the row just vanishes, so the
   *  caller can omit this. */
  onDeleted?: () => void;
  /** 'link' = inline pencil for list rows; 'button' = standalone outline. */
  variant?: 'link' | 'button';
}) {
  const [open, setOpen] = useState(false);
  const openDialog = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(true);
  };
  return (
    <>
      {variant === 'button' ? (
        <Button variant="outline" size="sm" onClick={openDialog}>
          <Pencil className="w-3.5 h-3.5" />
          Edit task
        </Button>
      ) : (
        <button
          type="button"
          aria-label={`Edit ${task.title}`}
          onClick={openDialog}
          className="inline-flex items-center gap-1 text-[11px] text-ink-faint hover:text-accent shrink-0"
        >
          <Pencil className="w-3.5 h-3.5" />
          Edit
        </button>
      )}
      {open && (
        <TaskEditorDialog
          projectId={task.project_id}
          task={task}
          onClose={() => setOpen(false)}
          onDeleted={onDeleted}
        />
      )}
    </>
  );
}

function TaskEditorDialog({
  projectId,
  task,
  onClose,
  onDeleted,
}: {
  projectId: string;
  task?: Task;
  onClose: () => void;
  onDeleted?: () => void;
}) {
  const isEdit = !!task;
  const create = useCreateTask();
  const update = useUpdateTask(task?.id ?? '');
  const del = useDeleteTask();
  const { data: me } = useCurrentUser();
  // PMs request work (the server marks the task pending_confirmation); a
  // supervisor/admin adds committed tasks. The dialog adapts its wording.
  const isPm = me?.role === 'pm';

  const [type, setType] = useState<TaskType>(task?.type ?? 'install');
  const [subtype, setSubtype] = useState<string>(
    task ? subtypeOf(task) : defaultSubtype('install'),
  );
  const [title, setTitle] = useState(task?.title ?? '');
  const [crewSize, setCrewSize] = useState(String(task?.crew_size_needed ?? 1));
  const [hours, setHours] = useState(
    task?.estimated_duration_minutes != null
      ? String(task.estimated_duration_minutes / 60)
      : '',
  );
  const [description, setDescription] = useState(task?.description ?? '');
  const [sapRma, setSapRma] = useState(
    task?.type === 'pickup' ? (task.sap_rma_reference ?? '') : '',
  );
  const [pickupLocations, setPickupLocations] = useState<PickupLocation[]>(
    task?.type === 'pickup' ? task.pickup_locations : [],
  );
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Staged attachments (demo #7 + demo #9): files held here, then uploaded once
  // the task exists. On add, they upload after the task is created; on edit they
  // upload to the existing task on Save — so a supervisor can attach files from
  // the task list, the same as on a new task.
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);

  const busy = create.isPending || update.isPending || del.isPending || uploading;
  const canSave = title.trim().length > 0 && !busy;

  function addFiles(incoming: File[]) {
    setFiles((prev) => {
      const next = [...prev];
      for (const f of incoming) {
        if (next.length >= 8) {
          toast.warning('Up to 8 files.');
          break;
        }
        if (!(f.type === 'application/pdf' || f.type.startsWith('image/'))) {
          toast.warning(`${f.name}: only images and PDFs can be attached.`);
          continue;
        }
        if (f.size > 25 * 1024 * 1024) {
          toast.warning(`${f.name} is too large (max 25 MB).`);
          continue;
        }
        if (next.some((x) => x.name === f.name && x.size === f.size)) continue;
        next.push(f);
      }
      return next;
    });
  }
  const showRma = type === 'pickup' && subtype === 'rma';
  const canDelete = isEdit && task ? taskDeletable(task) : false;

  function parsedMinutes(): number | null {
    const h = parseFloat(hours);
    return hours.trim() && Number.isFinite(h) && h > 0 ? Math.round(h * 60) : null;
  }
  function parsedCrew(): number {
    const n = parseInt(crewSize, 10);
    return Number.isFinite(n) ? Math.max(0, Math.min(20, n)) : 1;
  }
  /** Drop half-filled rows (a freshly-added blank stop) before persisting. */
  function cleanedPickupLocations(): PickupLocation[] {
    return pickupLocations.filter((l) => l.name.trim().length > 0);
  }

  function buildCreateBody(): TaskCreateInput {
    const base = {
      project_id: projectId,
      title: title.trim(),
      description: description.trim() || null,
      estimated_duration_minutes: parsedMinutes(),
      crew_size_needed: parsedCrew(),
    };
    switch (type) {
      case 'pickup':
        return {
          ...base,
          type: 'pickup',
          subtype: subtype as PickupSubtype,
          sap_rma_reference: subtype === 'rma' ? sapRma.trim() || null : null,
          pickup_locations: cleanedPickupLocations(),
        };
      case 'install':
        return { ...base, type: 'install', subtype: subtype as InstallSubtype };
      case 'punch':
        return { ...base, type: 'punch' };
      case 'labor':
        return { ...base, type: 'labor' };
    }
  }

  /** Upload the staged files to a task that now exists; returns the fail count. */
  async function uploadStaged(taskId: string): Promise<number> {
    if (files.length === 0) return 0;
    let failed = 0;
    setUploading(true);
    for (const f of files) {
      try {
        await uploadTaskMedia(taskId, f);
      } catch {
        failed += 1;
      }
    }
    setUploading(false);
    return failed;
  }

  function save() {
    if (!canSave) return;
    if (isEdit) {
      update.mutate(
        {
          title: title.trim(),
          description: description.trim() || null,
          crew_size_needed: parsedCrew(),
          estimated_duration_minutes: parsedMinutes(),
          ...(type === 'pickup' ? { pickup_locations: cleanedPickupLocations() } : {}),
        },
        {
          onSuccess: async () => {
            // Attach any newly-staged files to the existing task.
            const failed = await uploadStaged(task!.id);
            if (failed > 0) {
              toast.warning(
                `Task updated, but ${failed} file(s) couldn't be attached — try again from the task.`,
              );
            } else {
              toast.success('Task updated');
            }
            onClose();
          },
          onError: (err) => toast.error(`Update failed: ${err.message}`),
        },
      );
    } else {
      create.mutate(buildCreateBody(), {
        onSuccess: async (created) => {
          // Upload any staged attachments now that the task exists.
          const failed = await uploadStaged(created.id);
          const noun = isPm ? 'Task requested · pending confirmation' : 'Task added';
          if (failed > 0) {
            toast.warning(`${noun}, but ${failed} file(s) couldn't be attached — add them from the task.`);
          } else {
            toast.success(noun);
          }
          onClose();
        },
        onError: (err) => toast.error(`${isPm ? 'Request' : 'Add'} failed: ${err.message}`),
      });
    }
  }

  function remove() {
    if (!task) return;
    del.mutate(task.id, {
      onSuccess: () => {
        toast.success('Task removed');
        onClose();
        onDeleted?.();
      },
      onError: (err) => toast.error(`Remove failed: ${err.message}`),
    });
  }

  return (
    <Dialog
      open
      onClose={onClose}
      busy={busy}
      title={isEdit ? 'Edit task' : isPm ? 'Request a task' : 'Add task'}
    >
        <div className="p-4 space-y-3">
          {/* Type + subtype: editable on add, fixed on edit. */}
          {isEdit ? (
            <div className="text-[11px] text-ink-faint">
              <span className="inline-flex items-center rounded border border-line bg-fill-soft px-1.5 py-0.5 capitalize text-ink-dim">
                {task!.type}
                {subtype ? ` · ${subtype}` : ''}
              </span>
              <span className="ml-2">Type is fixed — delete &amp; re-add to change it.</span>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="task-type">Type</Label>
                <select
                  id="task-type"
                  className={SELECT_CLS}
                  value={type}
                  onChange={(e) => {
                    const next = e.target.value as TaskType;
                    setType(next);
                    setSubtype(defaultSubtype(next));
                  }}
                >
                  {TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              {(type === 'pickup' || type === 'install') && (
                <div>
                  <Label htmlFor="task-subtype">Subtype</Label>
                  <select
                    id="task-subtype"
                    className={SELECT_CLS}
                    value={subtype}
                    onChange={(e) => setSubtype(e.target.value)}
                  >
                    {(type === 'pickup' ? PICKUP_SUBTYPES : INSTALL_SUBTYPES).map((s) => (
                      <option key={s} value={s} className="capitalize">
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}

          <div>
            <Label htmlFor="task-title">Title</Label>
            <Input
              id="task-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={`e.g. ${TYPE_OPTIONS.find((o) => o.value === type)?.label ?? 'Task'} — second floor`}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="task-crew">Crew size</Label>
              <Input
                id="task-crew"
                type="number"
                min={0}
                max={20}
                value={crewSize}
                onChange={(e) => setCrewSize(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="task-hours">Est. hours</Label>
              <Input
                id="task-hours"
                type="number"
                min={0}
                step={0.5}
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                placeholder="optional"
              />
            </div>
          </div>

          {showRma && (
            <div>
              <Label htmlFor="task-rma">RMA reference</Label>
              <Input
                id="task-rma"
                value={sapRma}
                onChange={(e) => setSapRma(e.target.value)}
                placeholder="SAP RMA #"
              />
            </div>
          )}

          {type === 'pickup' && (
            <PickupLocationsEditor value={pickupLocations} onChange={setPickupLocations} />
          )}

          <div>
            <Label htmlFor="task-desc">Description</Label>
            <Textarea
              id="task-desc"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {/* Attachments — images, PDFs, or a straight-from-camera photo. On add
              they upload after the task is created; on edit they attach to the
              task on Save. */}
          {(
            <div>
              <Label>{isEdit ? 'Add attachments (optional)' : 'Attachments (optional)'}</Label>
              {files.length > 0 && (
                <ul className="space-y-1 my-1.5">
                  {files.map((f, i) => (
                    <li
                      key={i}
                      className="flex items-center gap-2 px-3 py-1.5 rounded border border-line"
                    >
                      {f.type.startsWith('image/') ? (
                        <ImageIcon className="w-3.5 h-3.5 text-ink-faint shrink-0" />
                      ) : (
                        <FileText className="w-3.5 h-3.5 text-ink-faint shrink-0" />
                      )}
                      <span className="text-sm truncate flex-1">{f.name}</span>
                      <button
                        type="button"
                        onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}
                        aria-label={`Remove ${f.name}`}
                        className="text-ink-faint hover:text-ink"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,application/pdf"
                multiple
                className="hidden"
                onChange={(e) => {
                  addFiles(Array.from(e.target.files ?? []));
                  e.target.value = '';
                }}
              />
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => {
                  addFiles(Array.from(e.target.files ?? []));
                  e.target.value = '';
                }}
              />
              <div className="flex gap-2 mt-1">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Paperclip className="w-3.5 h-3.5" /> Attach
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => cameraInputRef.current?.click()}
                >
                  <Camera className="w-3.5 h-3.5" /> Camera
                </Button>
              </div>
            </div>
          )}

          {!isEdit && (
            <p className="text-[11px] text-ink-faint">
              {isPm
                ? 'This sends a request — a supervisor confirms it, then schedules it and assigns crew.'
                : 'The task is added unscheduled — set its date with Schedule, then assign crew.'}
            </p>
          )}

          <div className="flex items-center justify-between gap-2 pt-1">
            {/* Destructive zone (edit mode) — two-step confirm, only when the
                task hasn't started. */}
            {isEdit && canDelete ? (
              confirmDelete ? (
                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-[11px] font-medium text-status-blocked-fg hover:underline disabled:opacity-50"
                  disabled={busy}
                  onClick={remove}
                >
                  {del.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  <Trash2 className="w-3.5 h-3.5" />
                  Confirm delete
                </button>
              ) : (
                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-[11px] text-ink-faint hover:text-status-blocked-fg disabled:opacity-50"
                  disabled={busy}
                  onClick={() => setConfirmDelete(true)}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Delete task
                </button>
              )
            ) : isEdit ? (
              <span className="text-[11px] text-ink-faint">
                Underway — cancel from the task screen.
              </span>
            ) : (
              <span />
            )}

            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={onClose} disabled={busy}>
                Cancel
              </Button>
              <Button size="sm" onClick={save} disabled={!canSave}>
                {(create.isPending || update.isPending) && (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                )}
                {isEdit ? 'Save' : isPm ? 'Request task' : 'Add task'}
              </Button>
            </div>
          </div>
        </div>
    </Dialog>
  );
}
